<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { computed, toRefs, useTemplateRef } from 'vue'
  import { useDeckStore } from '@/store'
  import { useIntervalFn, useNow } from '@vueuse/core'
  import { RELATIVE_TIME_REFRESH_INTERVAL } from '@/config/deck/constants'
  import { Urgency } from '@/store/parser'
  import type { RouteLocationRaw } from 'vue-router'
  import { useSearchHighlight } from '@/composables/useSearchHighlight'

  const { t, locale } = useI18n({ useScope: 'global' })

  const props = withDefaults(
    defineProps<{
      docId: string
      columnId?: string
      linkTo?: RouteLocationRaw
      searchTerms?: string[]
    }>(),
    {
      columnId: undefined,
      linkTo: undefined,
      searchTerms: () => []
    }
  )

  const { docId, columnId, searchTerms } = toRefs(props)
  const deckStore = useDeckStore()
  const { getDocumentById, documentExistsById } = deckStore

  // Ne pas throw si le doc n'est pas encore hydraté : le liveQuery Dexie peut référencer un docId avant que documents.value soit à jour.
  const doc = computed(() =>
    documentExistsById(docId.value) ? getDocumentById(docId.value) : null
  )

  const headlineEl = useTemplateRef('headline')
  const leadEl = useTemplateRef('lead')
  useSearchHighlight(
    () => [headlineEl.value, leadEl.value],
    searchTerms,
    () => doc.value
  )

  const now = useNow({
    scheduler: cb => useIntervalFn(cb, RELATIVE_TIME_REFRESH_INTERVAL)
  })

  // Priorité croissante : la plus petite variante adaptée à une card d'abord
  const ROLE_PRIORITY = ['Thumbnail', 'Preview', 'HighDef']

  const PreviewMedia = computed(() => {
    if (!doc.value) return false
    if (
      doc.value.type === 'photo' ||
      doc.value.type === 'graphic' ||
      doc.value.type === 'video'
    ) {
      const sizes = doc.value.media.pictureSizes
      return ROLE_PRIORITY.map(role =>
        sizes.find(size => size.role === role)
      ).find(Boolean)
    }
    if (doc.value.type === 'multimedia' || doc.value.type === 'webstory') {
      if (doc.value.medias.length === 0 || !doc.value.medias[0]) return false
      return doc.value.medias[0].sizes.find(size => size.role === 'Preview')
    }
    return false
  })
</script>

<template>
  <RouterLink
    v-if="doc"
    :class="{
      'border-flash border-l-12':
        doc.type === 'text' && doc.urgency === Urgency.Flash,
      'border-alert border-l-12':
        doc.type === 'text' && doc.urgency === Urgency.Alert,
      'border-urgent border-l-12':
        doc.type === 'text' && doc.urgency === Urgency.Urgent,
      'border-lead border-l-12':
        (doc.type === 'text' || doc.type === 'multimedia') &&
        doc.hasBeenAlerted,
      'pt-2': !PreviewMedia
    }"
    :lang="doc.lang"
    :dir="doc.lang === 'ar' ? 'rtl' : 'ltr'"
    :to="
      linkTo ?? {
        name: 'document',
        params: { docId },
        state: { columnId }
      }
    "
    class="group card bg-base-100 mb-3 pb-2 shadow transition-shadow hover:shadow-md"
  >
    <img
      v-if="PreviewMedia"
      :src="PreviewMedia.href"
      :width="PreviewMedia.width"
      :height="PreviewMedia.height"
      loading="lazy"
      decoding="async"
      alt=""
      class="bg-base-200 mb-2 aspect-video w-full object-cover"
    />
    <div
      v-else-if="
        doc.type === 'photo' || doc.type === 'graphic' || doc.type === 'video'
      "
      class="bg-base-200 mb-2 aspect-video w-full"
    ></div>
    <div class="card-body px-4 py-3">
      <div
        class="flex items-center justify-between gap-2 px-2 text-xs text-slate-400"
      >
        <p
          v-if="doc.embargoed && doc.embargoed > new Date()"
          :key="`date-${locale}-embargo`"
        >
          Embargo : {{ $dayjs.fromNow(doc.embargoed, now) }}
        </p>
        <p
          v-else
          :key="`date-${locale}`"
        >
          {{ $dayjs.fromNow(doc.published, now) }}
        </p>
        <div class="flex items-center gap-2">
          <p
            v-if="doc.type === 'photo' && doc.topshot"
            class="text-right font-semibold uppercase"
          >
            Topshot
          </p>
          <p
            v-else-if="
              (doc.type === 'text' ||
                doc.type === 'multimedia' ||
                doc.type === 'factcheck') &&
              doc.genre
            "
            class="text-right font-semibold uppercase"
          >
            <span>
              {{ doc.genre }}
            </span>
          </p>
        </div>
      </div>
      <h2
        v-if="doc.type !== 'photo'"
        ref="headline"
        class="card-title mt-1 text-[1.4rem] leading-[1.4rem] tracking-[-0.045em] wrap-anywhere group-visited:text-gray-400"
        :class="{
          'line-through': doc.status === 'Canceled'
        }"
      >
        {{ doc.headline }}
      </h2>
      <p
        v-if="
          (doc.type === 'text' || doc.type === 'multimedia') &&
          doc.urgency > 2 &&
          doc.status === 'Usable'
        "
        ref="lead"
        class="line-clamp-3 text-[1rem] leading-5 tracking-[-0.0063rem] group-visited:text-gray-400"
      >
        {{ doc.paragraphs[0] }}
      </p>
      <div
        v-if="doc.signal == 'correction'"
        class="badge badge-warning"
      >
        {{ t('document.correction') }}
      </div>
      <div
        v-if="doc.status == 'Canceled'"
        class="badge badge-error"
      >
        {{ t('document.canceled') }}
      </div>
    </div>
  </RouterLink>
</template>
